import type { AuxState } from './channels';

export interface KeyEventLike {
  type: 'keydown' | 'keyup';
  code: string;
}

export interface KeyboardSample {
  axes: {
    roll: number;
    pitch: number;
    yaw: number;
    throttle: number;
  };
  aux: AuxState;
}

/** Time for a centred keyboard flight axis to travel from 0 to full scale. */
export const KEYBOARD_RAMP_SECONDS = 0.2;

const moveToward = (current: number, target: number, maximumDelta: number): number => {
  if (current < target) return Math.min(target, current + maximumDelta);
  if (current > target) return Math.max(target, current - maximumDelta);
  return current;
};

/** Keyboard state adapter. Call handleKey from injected DOM listeners. */
export class KeyboardSource {
  private readonly held = new Set<string>();
  private roll = 0;
  private pitch = 0;
  private yaw = 0;
  private throttle = 0;

  handleKey(event: KeyEventLike): void {
    if (event.type === 'keydown') this.held.add(event.code);
    else this.held.delete(event.code);
  }

  sample(dt = 1 / 60): KeyboardSample {
    if (!Number.isFinite(dt) || dt < 0) {
      throw new RangeError('keyboard sample dt must be finite and non-negative');
    }
    const delta = dt / KEYBOARD_RAMP_SECONDS;
    this.roll = moveToward(this.roll, this.direction('KeyD', 'KeyA'), delta);
    this.pitch = moveToward(this.pitch, this.direction('KeyW', 'KeyS'), delta);
    this.yaw = moveToward(this.yaw, this.direction('KeyE', 'KeyQ'), delta);

    const throttleUp =
      this.held.has('ArrowUp') || this.held.has('ShiftLeft') || this.held.has('ShiftRight');
    const throttleDown =
      this.held.has('ArrowDown') || this.held.has('ControlLeft') || this.held.has('ControlRight');
    if (throttleUp !== throttleDown) {
      this.throttle = moveToward(this.throttle, throttleUp ? 1 : 0, delta);
    }

    return {
      axes: {
        roll: this.roll,
        pitch: this.pitch,
        yaw: this.yaw,
        throttle: this.throttle,
      },
      aux: this.auxState(),
    };
  }

  private direction(positiveCode: string, negativeCode: string): number {
    return Number(this.held.has(positiveCode)) - Number(this.held.has(negativeCode));
  }

  private auxState(): AuxState {
    return {
      arm: this.held.has('Space'),
      modeToggle: this.held.has('KeyM'),
      reset: this.held.has('KeyR'),
      releaseManual: this.held.has('KeyX'),
    };
  }
}
